import type { SceneEditor } from '@zakeke/zakeke-3d-core';
import { createTextureReferenceDesignItems, downloadTextureReference } from './textureReferenceDownloader';

function getAllSides(product: MPlaza.Model) {
    const sides: MPlaza.Side[] = [];

    product.get('colors').each((color) => {
        color.get('sides').each((side) => {
            if (!sides.find((x) => x.get('sideID') == side.get('sideID'))) {
                sides.push(side);
            }
        });
    });

    return sides;
}

export class DesignManager {
    private zkDesign!: MPlaza.Design;
    private zkModel!: MPlaza.Model;
    private zkScene!: Zakeke.Scene;
    private editor!: SceneEditor;
    private paper!: paper.PaperScope;
    private hiddenMode!: boolean;

    constructor(product: MPlaza.Model, editor: SceneEditor, scene: Zakeke.Scene) {
        this.zkModel = product;
        this.zkScene = scene;
        this.editor = editor;

        this.zkDesign = new MPlaza.Design();
        this.zkDesign.setModel(product);
        this.paper = new paper.PaperScope();
        this.paper.setup(new this.paper.Size(10, 10));
    }

    public setHiddenMode(hiddenMode: boolean) {
        console.log('Setting hidden mode to', hiddenMode);
        this.hiddenMode = hiddenMode;
        if (hiddenMode) {
            this.clearDesignItems();
        }
        this.render();
    }

    public replaceModel(product: MPlaza.Model) {
        this.zkModel = product;
        this.zkDesign.setModel(product);
    }

    private clearDesignItems() {
        const designItems: MPlaza.DesignItem[] = this.zkDesign.get('designItems').models;
        const designItemsToRemove: MPlaza.DesignItem[] = [];

        // Remove
        for (let designItem of designItems) {
            designItemsToRemove.push(designItem);
        }

        for (let designItem of designItemsToRemove) {
            this.zkDesign.get('designItems').remove(designItem);
            this.paper.project?.activeLayer?.children.find((x) => x.data.guid == designItem.get('itemGuid'))?.remove();
        }
    }

    /**
     * Build the design items to use as preview on the 3D model and renders it.
     * We can't generate all items for all variants at once because the
     * check to show only items of the pair variantId - sideId has been removed on
     * the customer for the design on multiple products. So we need to generate
     * the previews for a variant at time.
     * @param variantId The variant id to use for the sides previews
     * @returns
     */
    public async refreshSides(variantId: number) {
        console.log('Refreshing sides', variantId);
        if (this.zkModel.get('scenes').length == 0) return;

        this.clearDesignItems();

        if (this.hiddenMode) return;

        // Create a design item for each side and variant if a mesh side exists
        // Here we don't use the meshSideIds that we have created before because
        // a mesh side can have a variantID null, so we don't know which side to use
        // So just iterate all variants, and if we repeat the same design item for the same side on multiple variants
        // is not a problem
        const modelColor = this.zkModel.get('colors').get(variantId);

        const designItems: MPlaza.DesignItem[] = this.zkDesign.get('designItems').models;

        const meshSidesIds = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .map((x) => [x.get('colorID'), x.get('sideID')]);

        for (const modelSide of modelColor.get('sides').toArray()) {
            // Check if a meshSide exist for this variant and side or for side and variant null
            const variantMeshSide = meshSidesIds.find((x) => x[0] === modelColor.get('colorID') && x[1] === modelSide.get('sideID'));
            const baseMeshSide = meshSidesIds.find((x) => x[0] == null && x[1] === modelSide.get('sideID'));

            if (!variantMeshSide && !baseMeshSide) continue;

            if (
                !designItems.find(
                    (x) => x.get('areas').at(0).get('colorID') === modelColor.get('colorID') && x.get('areas').at(0).get('sideID') == modelSide.get('sideID')
                )
            ) {
                console.log('Creating design item for side', modelSide.get('sideID'), 'variant', modelColor.get('colorID'));
                const newDesignItems = this.createPreviewItemForSide(modelSide);

                let index = 1;
                for (const designItem of newDesignItems) {
                    designItem.set('index', index++);
                    this.zkDesign.get('designItems').push(designItem);
                }
            }
        }

        await this.render();
    }

    public async render() {
        this.editor.resetDesign();
        await this.editor.renderDesignAsync(this.zkDesign);
    }

    public getRotation(meshId: string) {
        const mesh = this.editor.getMeshByID(meshId)!;
        const transform = this.getPaperViewMatrix(mesh);
        let rotation = transform.rotation;

        if (rotation < 0) rotation += 360;

        return rotation;
    }

    public setRotation(meshId: string, angle: number) {
        const mesh = this.editor.getMeshByID(meshId)!;
        let { x, y, width, height } = this.getAreaBounds(mesh);

        this.editor.rotateDesignView(mesh, mesh.subMeshes[0], angle - this.getRotation(meshId), x + width / 2, y + height / 2);
        this.render();
    }

    public getPosition(meshId: string) {
        const mesh = this.editor.getMeshByID(meshId)!;
        const transform = this.getPaperViewMatrix(mesh);

        return { x: transform.positionX, y: transform.positionY };
    }

    public setPosition(meshId: string, x: number, y: number) {
        const mesh = this.editor.getMeshByID(meshId)!;

        const currentPosition = this.getPosition(meshId);
        const delta = new this.paper.Point(x - currentPosition.x, y - currentPosition.y);

        this.editor.translateDesignView(mesh, mesh.subMeshes[0], delta.x, delta.y);
        this.render();
    }

    public getScaling(meshId: string) {
        const mesh = this.editor.getMeshByID(meshId)!;
        const transform = this.getPaperViewMatrix(mesh);

        return { x: transform.scalingX, y: transform.scalingY };
    }

    public setScaling(meshId: string, h: number, v: number) {
        const mesh = this.editor.getMeshByID(meshId)!;
        const { x, y, width, height } = this.getAreaBounds(mesh);

        const currentScaling = this.getScaling(meshId);
        const delta = new this.paper.Point(h / currentScaling.x, v / currentScaling.y);

        this.editor.scaleDesignView(mesh, mesh.subMeshes[0], delta.x, delta.y, x + width / 2, y + height / 2);
        this.render();
    }

    public setTransform(meshId: string, positionX: number, positionY: number, angle: number, scalingX: number, scalingY: number) {
        console.log('Applying transform:', {
            positionX,
            positionY,
            angle,
            scalingX,
            scalingY
        });

        const mesh = this.editor.getMeshByID(meshId)!;
        const { x, y, width, height } = this.getAreaBounds(mesh);

        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == mesh.id);

        const customResolution = this.getDesktopTextureResolution(meshId) || this.getMobileTextureResolution(meshId);
        const ratio = customResolution / 1024;

        let matrix = new this.paper.Matrix();
        matrix = matrix.scale(1 / ratio, 1 / ratio);
        matrix = matrix.translate(positionX, positionY);
        matrix = matrix.rotate(angle, x + width / 2, y + height / 2);
        matrix = matrix.scale(scalingX, scalingY);

        meshSide?.set('matrix', JSON.stringify(matrix.values));
        meshSide?.set('transform', {
            positionX,
            positionY,
            rotation: angle,
            scalingX: scalingX,
            scalingY: scalingY
        });
        this.render();
    }

    public async resetTransform(meshId: string) {
        console.log('Resetting transform', meshId);
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        meshSide?.set('matrix', '');
        meshSide?.set('transform', null);

        await this.render();

        // After render, set the current values
        const currentTransform = this.getPaperViewMatrix(this.editor.getMeshByID(meshId)!);
        this.setTransform(
            meshId,
            currentTransform.positionX,
            currentTransform.positionY,
            currentTransform.rotation,
            currentTransform.scalingX,
            currentTransform.scalingY
        );
    }

    public isInternalCustomizerReady(mesh: BABYLON.AbstractMesh) {
        if (!mesh) return false;

        const meshDesignManager = this.editor.getMeshDesignMananger(mesh as BABYLON.Mesh, mesh.subMeshes?.[0]);
        return Boolean(meshDesignManager?.getInternalCustomizer());
    }

    // Texture resolution
    public getIsCustomTextureResolution(meshId: string) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        if (meshSide) {
            const hasCustomDesktopTextureResolution = meshSide.getConfig()?.getGeneralConfig()?.get('desktopTextureResolutionWidth') != null;
            const hasCustomMobileTextureResolution = meshSide.getConfig()?.getGeneralConfig()?.get('mobileTextureResolutionWidth') != null;

            return hasCustomDesktopTextureResolution || hasCustomMobileTextureResolution;
        } else {
            throw new Error('Mesh side not found for mesh ' + meshId);
        }
    }

    public getDesktopTextureResolution(meshId: string) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        if (meshSide) {
            return meshSide.getOrCreateConfig().getDesktopTextureResolutionWidth();
        } else {
            throw new Error('Mesh side not found for mesh ' + meshId);
        }
    }

    public setDesktopTextureResolution(meshId: string, resolution: number | null) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        if (meshSide) {
            meshSide.getOrCreateConfig().setDesktopTextureResolutionWidth(resolution);
            meshSide.getOrCreateConfig().setDesktopTextureResolutionHeight(resolution);
        } else {
            throw new Error('Mesh side not found for mesh ' + meshId);
        }
    }

    public getMobileTextureResolution(meshId: string) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        if (meshSide) {
            return meshSide.getOrCreateConfig().getMobileTextureResolutionWidth();
        } else {
            throw new Error('Mesh side not found for mesh ' + meshId);
        }
    }

    public setMobileTextureResolution(meshId: string, resolution: number | null) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == meshId);

        if (meshSide) {
            meshSide.getOrCreateConfig().setMobileTextureResolutionWidth(resolution);
            meshSide.getOrCreateConfig().setMobileTextureResolutionHeight(resolution);
        } else {
            throw new Error('Mesh side not found for mesh ' + meshId);
        }
    }

    // Private methods
    private getAreaBounds(mesh: any) {
        // Area
        let meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == mesh.id);

        if (meshSide) {
            let sideID = meshSide.get('sideID');
            let area = getAllSides(this.zkModel)
                .find((x) => x.get('sideID') == sideID)!
                .get('areas')
                .at(0);

            // Bounds
            let x = area.get('boundsX');
            let y = area.get('boundsY');
            let width = area.get('boundsWidth');
            let height = area.get('boundsHeight');

            return { x, y, width, height };
        } else throw 'Mesh side not found for mesh ' + mesh.id;
    }

    private createPreviewItemForSide(side: MPlaza.Side) {
        this.paper.activate();
        return createTextureReferenceDesignItems(this.paper, side);
    }

    private getPaperViewMatrix(mesh: BABYLON.AbstractMesh) {
        const meshSide = this.zkModel
            .get('scenes')
            .at(0)
            .get('meshSides')
            .find((x) => x.get('meshID') == mesh.id);

        const meshDesignManager = this.editor.getMeshDesignMananger(mesh as BABYLON.Mesh, mesh.subMeshes[0]);

        const existingTransform = meshSide?.get('transform');

        if (existingTransform) {
            return existingTransform;
        }
        const customizer = meshDesignManager!.getInternalCustomizer()!;

        const startMatrix = customizer.paper.view.matrix as paper.Matrix;

        return {
            positionX: startMatrix.translation.x,
            positionY: startMatrix.translation.y,
            rotation: startMatrix.rotation,
            scalingX: startMatrix.scaling.x,
            scalingY: startMatrix.scaling.y
        };
    }

    public async downloadReferenceTexture(variantId: number, sideId: number) {
        await downloadTextureReference(this.zkModel, variantId, sideId);
    }
}
